import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Clock, TrendingUp, X, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

const SearchBox = ({ className = '', autoFocus = false, onSearchComplete }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const debouncedQuery = useDebounce(query, 300);

  // Auto focus for mobile
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
        setIsOpen(true);
      }, 100);
    }
  }, [autoFocus]);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('pazarlo_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Fetch suggestions when query changes
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      fetchSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
    }
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (searchTerm) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/all-products?search=${encodeURIComponent(searchTerm)}&limit=8`);
      const products = response.data.products || [];
      
      // Create unique suggestions from product names, brands, and OEM codes
      const suggestionSet = new Set();
      const results = [];
      
      products.forEach(p => {
        // Add product name (truncated)
        const name = p.name?.substring(0, 60);
        if (name && !suggestionSet.has(name.toLowerCase())) {
          suggestionSet.add(name.toLowerCase());
          results.push({
            type: 'product',
            text: name,
            id: p.id,
            brand: p.brand,
            oem: p.oem_code,
            price: p.display_price || p.best_price
          });
        }
        
        // Add OEM code if matches
        if (p.oem_code && p.oem_code.toLowerCase().includes(searchTerm.toLowerCase())) {
          const oemKey = p.oem_code.toLowerCase();
          if (!suggestionSet.has(oemKey)) {
            suggestionSet.add(oemKey);
            results.push({
              type: 'oem',
              text: p.oem_code,
              count: 1
            });
          }
        }
        
        // Add brand if matches
        if (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase())) {
          const brandKey = `brand:${p.brand.toLowerCase()}`;
          if (!suggestionSet.has(brandKey)) {
            suggestionSet.add(brandKey);
            results.push({
              type: 'brand',
              text: p.brand,
              count: response.data.total
            });
          }
        }
      });
      
      setSuggestions(results.slice(0, 8));
    } catch (error) {
      console.error('Suggestion error:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveRecentSearch = (term) => {
    const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('pazarlo_recent_searches', JSON.stringify(updated));
  };

  const handleSearch = (searchTerm) => {
    if (searchTerm?.trim()) {
      saveRecentSearch(searchTerm.trim());
      setIsOpen(false);
      navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
      if (onSearchComplete) onSearchComplete();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch(query);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'product') {
      navigate(`/products/${suggestion.id}`);
      if (onSearchComplete) onSearchComplete();
    } else {
      handleSearch(suggestion.text);
    }
    setIsOpen(false);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('pazarlo_recent_searches');
  };

  const removeRecentSearch = (term, e) => {
    e.stopPropagation();
    const updated = recentSearches.filter(s => s !== term);
    setRecentSearches(updated);
    localStorage.setItem('pazarlo_recent_searches', JSON.stringify(updated));
  };

  const popularSearches = ['BOSCH', 'MANN', 'VALEO', 'HAVA FİLTRESİ', 'YAĞ FİLTRESİ'];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Yedek parça, OEM numarası ara..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            className="w-full pl-4 pr-12 py-3 md:py-6 rounded-full border-2 border-slate-200 focus:border-orange-500 text-base h-12 md:h-auto"
            data-testid="mobile-search-input"
          />
          <Button 
            type="submit"
            size="icon"
            className="absolute right-1.5 md:right-2 top-1/2 -translate-y-1/2 bg-orange-500 hover:bg-orange-600 rounded-full h-9 w-9 md:h-10 md:w-10"
            data-testid="search-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
            ) : (
              <Search className="h-4 w-4 md:h-5 md:w-5" />
            )}
          </Button>
        </div>
      </form>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-[9999] max-h-[60vh] md:max-h-[70vh] overflow-y-auto">
          
          {/* Loading indicator */}
          {loading && query.length >= 2 && (
            <div className="p-4 text-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Aranıyor...
            </div>
          )}

          {/* Suggestions */}
          {!loading && suggestions.length > 0 && (
            <div className="p-2">
              <div className="text-xs text-slate-400 px-3 py-2 font-medium">
                "{query}" için öneriler
              </div>
              {suggestions.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(item)}
                  className="w-full text-left px-3 py-3 hover:bg-orange-50 rounded-xl flex items-center gap-3 transition-colors group"
                >
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-orange-100 flex-shrink-0">
                    <Search className="h-4 w-4 text-slate-400 group-hover:text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate text-sm md:text-base">
                      {item.text}
                    </div>
                    {item.type === 'product' && item.brand && (
                      <div className="text-xs text-slate-500 truncate">
                        {item.brand} {item.oem && `• OEM: ${item.oem.substring(0, 15)}`}
                      </div>
                    )}
                    {item.type === 'brand' && (
                      <div className="text-xs text-orange-500">Marka</div>
                    )}
                    {item.type === 'oem' && (
                      <div className="text-xs text-blue-500">OEM Kodu</div>
                    )}
                  </div>
                  {item.type === 'product' && item.price && (
                    <div className="text-orange-600 font-bold text-sm flex-shrink-0">
                      ₺{item.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Recent Searches */}
          {!loading && query.length < 2 && recentSearches.length > 0 && (
            <div className="p-2 border-b">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Son Aramalar
                </span>
                <button 
                  onClick={clearRecentSearches}
                  className="text-xs text-orange-500 hover:text-orange-600"
                >
                  Temizle
                </button>
              </div>
              {recentSearches.map((term, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSearch(term)}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg flex items-center justify-between group"
                >
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-300" />
                    <span className="text-slate-700 text-sm">{term}</span>
                  </span>
                  <X 
                    className="h-4 w-4 text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100"
                    onClick={(e) => removeRecentSearch(term, e)}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Popular Searches */}
          {!loading && query.length < 2 && (
            <div className="p-2">
              <div className="text-xs text-slate-400 px-3 py-2 font-medium flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Popüler Aramalar
              </div>
              <div className="flex flex-wrap gap-2 px-3 py-2">
                {popularSearches.map((term, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSearch(term)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-orange-100 text-slate-700 hover:text-orange-700 rounded-full text-sm transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No results */}
          {!loading && query.length >= 2 && suggestions.length === 0 && (
            <div className="p-6 text-center">
              <div className="text-slate-400 mb-2 text-sm">"{query}" için sonuç bulunamadı</div>
              <button 
                onClick={() => handleSearch(query)}
                className="text-orange-500 hover:text-orange-600 font-medium text-sm"
              >
                Tüm ürünlerde ara →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
